/**
 * Session identity — `x-session-id` and `x-gtid`.
 *
 * Two correlation ids the Middleware uses to tie UI activity together:
 *
 *   sessionId  One per UI session, constant for its lifetime. Minted lazily on
 *              first use and held in sessionStorage so a refresh keeps the same
 *              id within the tab, while a new tab or a new login gets a new one.
 *
 *   gtid       One per *business transaction*, prefixed with the session id:
 *              `{sessionId}-tx-{nnnn}`. One user action = one GTID. A batch Run
 *              is a single GTID — the Middleware mints the per-member children
 *              from it, so the UI must not mint one per script.
 *
 * Per TONIC-PEX-IMPL-FE-001 Task 1.
 *
 * @module shared/services/session/sessionIdentity
 */

const SESSION_ID_KEY = 'tonic.sessionId';
const GTID_COUNTER_KEY = 'tonic.gtidCounter';

/** sessionStorage can throw in private mode — never let identity break a request. */
const safeGet = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key, value) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* in-memory fallback below keeps ids stable for the page's lifetime */
  }
};

// Fallback when sessionStorage is unavailable: ids stay stable until reload.
let memorySessionId = null;
let memoryCounter = 0;

const randomSuffix = () => Math.random().toString(36).slice(2, 10);

/**
 * The current session id, minted on first call.
 * @returns {string} e.g. "sess-ly8x2k9q-4f2a91cd"
 */
export const getSessionId = () => {
  const stored = safeGet(SESSION_ID_KEY);
  if (stored) return stored;
  if (memorySessionId) return memorySessionId;

  const minted = `sess-${Date.now().toString(36)}-${randomSuffix()}`;
  memorySessionId = minted;
  safeSet(SESSION_ID_KEY, minted);
  return minted;
};

/**
 * Mint the next GTID for a business transaction.
 *
 * Call once per user action and pass the same value to every request that
 * action fans out into — not once per request.
 *
 * @returns {string} e.g. "sess-ly8x2k9q-4f2a91cd-tx-0007"
 */
export const nextGtid = () => {
  const sessionId = getSessionId();
  const stored = parseInt(safeGet(GTID_COUNTER_KEY) ?? '', 10);
  const current = Number.isNaN(stored) ? memoryCounter : stored;
  const next = current + 1;

  memoryCounter = next;
  safeSet(GTID_COUNTER_KEY, String(next));

  return `${sessionId}-tx-${String(next).padStart(4, '0')}`;
};

/**
 * Drop the session identity — call on logout so the next login is a new session.
 */
export const resetSessionIdentity = () => {
  try {
    window.sessionStorage.removeItem(SESSION_ID_KEY);
    window.sessionStorage.removeItem(GTID_COUNTER_KEY);
  } catch {
    /* nothing persisted to clear */
  }
  memorySessionId = null;
  memoryCounter = 0;
};

const sessionIdentity = { getSessionId, nextGtid, resetSessionIdentity };

export default sessionIdentity;
